import { Button, Popover, PopoverSurface, PopoverTrigger, Text } from '@fluentui/react-components';
import {
  AlertRegular,
  BuildingRegular,
  PeopleRegular,
  Person28Regular,
} from '@fluentui/react-icons';
import cn from 'classnames';
import moment from 'moment-timezone';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { replaceClassNamePlaceholder } from '../../../Classbook/Classbook.utils';
import { TimetablePeriodProps, TimetablePeriodViewerRole } from './TimetablePeriod.interface';
import { isNotOccurringLesson } from './TimetablePeriod.utils';
import './TimetablePeriod.scss';
import { getKnownTeacherName } from './TimetablePeriod.utils';
import { TimetablePeriodInfoRow } from './TimetablePeriodInfoRow';
import { TimetablePeriodSubstitutionInfoRow } from './TimetablePeriodSubstitutionInfoRow';
import { TimetablePeriodTime } from './TimetablePeriodTime';

/**
 * Represents a period as a timetable event.
 * This component is designed to be used in @mnspro-cloud/components and
 * delegates navigation and permission handling to the parent via callbacks.
 */
export const TimetablePeriod: FC<TimetablePeriodProps> = ({
  eventData,
  timeTableLesson,
  viewerRole,
  substitution,
  isMobileView,
  lessonPattern,
  onNavigateToCoursebook,
  onNavigateToAbsences,
  onNavigateToSubstitution,
  canShowClassbook = false,
  canShowAbsences = false,
  canManageSubstitutions = false,
}) => {
  const { t } = useTranslation();
  const isFreePeriod = eventData.id.startsWith('free');
  const isBreak = eventData.id.startsWith('break');
  const isHoliday = eventData.id.startsWith('holiday');
  const lesson = substitution?.substitutionLesson ?? timeTableLesson;
  const classbookCourseId = timeTableLesson?.id;
  const isCancelled = isNotOccurringLesson(substitution);
  const cancelledHint = isCancelled
    ? t('timetable:period:lesson_not_occurring', {
        defaultValue: 'Diese Stunde entfällt.',
      })
    : undefined;

  const originalTeacher = getKnownTeacherName(timeTableLesson?.teacher);
  const substituteTeacher = getKnownTeacherName(substitution?.substitutionLesson?.teacher);
  const teacherName = substitution?.substitutionLesson ? substituteTeacher : originalTeacher;

  const title = useMemo(() => {
    if (!lesson) {
      return '';
    }

    return lesson.title || '';
  }, [lesson]);

  const classesList =
    lesson && lesson.classes
      ? lesson.classes.map((c) => replaceClassNamePlaceholder(c, t)).join(' ')
      : '';

  const showClassesLabel =
    lesson !== null &&
    // Students normally know the class they are in, teachers need the info
    // which class the lesson belongs to.
    viewerRole === TimetablePeriodViewerRole.Staff &&
    lesson.classes !== null &&
    lesson.classes !== undefined;

  const showRoomLabel =
    lesson !== null && lesson.room !== undefined && lesson.room !== null && lesson.room !== '';

  const showTeacherLabel = teacherName !== undefined;

  // Determines how many information there is to display below the
  // lesson course name. We need to know how many rows there are to potentially
  // display, so we're able to determine which infos to hide if the viewport does
  // not provide enough space (see container queries in Timetable.scss).
  const labelsCount =
    (showClassesLabel ? 1 : 0) + (showRoomLabel ? 1 : 0) + (showTeacherLabel ? 1 : 0);

  const trigger = (
    <div className={cn('timetablePeriod', 'fc-event-main-frame', `labels-count-${labelsCount}`)}>
      {!isFreePeriod && !isBreak && !isHoliday && (
        <TimetablePeriodTime
          eventData={eventData}
          timeTableLesson={lesson}
          substitution={substitution}
          lessonPattern={lessonPattern}
        />
      )}
      <div className="fc-event-title-container">
        {!isFreePeriod && !isBreak && !isHoliday && (
          <div className={cn('fc-event-title', { periodCancelled: isCancelled })}>{title}</div>
        )}
        {substitution?.remark && (
          <div className="fc-event-remark">
            {t('timetable:remark', { defaultValue: 'Hinweis' })}: {substitution.remark}
          </div>
        )}
        {(isHoliday || isBreak) && (
          <div className="fc-event-title">
            {isBreak ? t('break', { defaultValue: 'Pause' }) : eventData.title}
          </div>
        )}
        {isBreak && (
          <div className="fc-event-break-duration">
            {moment(eventData.startStr).format('HH:mm')} -{' '}
            {moment(eventData.endStr).format('HH:mm')}
          </div>
        )}
        {showClassesLabel && (
          <div
            className={cn('fc-event-classes', {
              periodCancelled: isCancelled,
            })}
          >
            {classesList}
          </div>
        )}
        {showRoomLabel && (
          <div
            className={cn('fc-event-room', {
              periodCancelled: isCancelled,
            })}
          >
            {lesson.room}
          </div>
        )}
        {showTeacherLabel && (
          <div
            className={cn('fc-event-teacher', {
              periodCancelled: isCancelled,
            })}
          >
            {teacherName}
          </div>
        )}
      </div>
    </div>
  );

  if (isFreePeriod || isBreak || isHoliday) {
    return trigger;
  }

  return (
    <Popover positioning={isMobileView ? 'above' : 'before'} withArrow>
      <PopoverTrigger disableButtonEnhancement>{trigger}</PopoverTrigger>
      <PopoverSurface>
        <div className="timeTablePeriodPopup flex flex-col">
          <div className={cn('flex', 'flex-col', isMobileView ? 'gap-1' : 'gap-2')}>
            <Text weight="bold" size={isMobileView ? 400 : 500} className="title" block>
              {eventData.title}
            </Text>
            <Text className="timespan" block>
              {`${moment(eventData.startStr).format('dddd, MMMM Do YYYY, HH:mm')} - ${moment(
                eventData.endStr,
              ).format('HH:mm')}`}
            </Text>
            {viewerRole === TimetablePeriodViewerRole.Staff &&
              lesson &&
              lesson.id && // Aufsichten können nur durch id = null herausgefiltert werden
              lesson.lessonId && (
                <div className="btnsWrapper flex gap-2">
                  {canShowClassbook && onNavigateToCoursebook && (
                    <Button
                      disabled={isCancelled}
                      title={cancelledHint}
                      aria-label={t('timetable:period:goto_coursebook_label', {
                        defaultValue: 'Navigation zur Unterseite ins Kursbuch: {{title}}',
                        title: lesson.title,
                      })}
                      onClick={() => {
                        if (lesson && lesson.id && lesson.lessonId) {
                          onNavigateToCoursebook(
                            lesson.id,
                            lesson.multiLessonId ?? lesson.lessonId,
                          );
                        }
                      }}
                    >
                      {t('timetable:period:goto_coursebook', { defaultValue: 'Kursbuch anzeigen' })}
                    </Button>
                  )}
                  {classbookCourseId && canShowAbsences && onNavigateToAbsences && (
                    <Button
                      disabled={isCancelled}
                      title={cancelledHint}
                      onClick={() => {
                        onNavigateToAbsences(classbookCourseId);
                      }}
                    >
                      {t('timetable:period:goto_cours_absences', { defaultValue: 'Absenzen' })}
                    </Button>
                  )}
                  {canManageSubstitutions && onNavigateToSubstitution && (
                    <Button
                      aria-label={t('timetable:period:createSubstitution_label', {
                        defaultValue: 'Öffnet Unterseite zum eintragen einer Vertretung',
                      })}
                      onClick={() => {
                        if (lesson && lesson.lessonId) {
                          onNavigateToSubstitution(lesson.lessonId, substitution !== null);
                        }
                      }}
                    >
                      {substitution === null
                        ? t('timetable:period:createSubstitution', {
                            defaultValue: 'Vertretung eintragen',
                          })
                        : t('timetable:period:editSubstitution', {
                            defaultValue: 'Vertretung bearbeiten',
                          })}
                    </Button>
                  )}
                </div>
              )}
          </div>
          {timeTableLesson && (
            <div className="flex flex-col">
              {!timeTableLesson.event && substitution && (
                <TimetablePeriodSubstitutionInfoRow substitution={substitution} />
              )}
              {timeTableLesson.event && substitution?.remark && (
                <TimetablePeriodInfoRow
                  icon={<AlertRegular />}
                  label={t('timetable:period:remark', { defaultValue: 'Hinweis' })}
                  value={<div>{substitution.remark}</div>}
                />
              )}
              {(originalTeacher || substituteTeacher) && !timeTableLesson.event && (
                <TimetablePeriodInfoRow
                  icon={<Person28Regular />}
                  label={t('timetable:period:teacher', { defaultValue: 'Kurslehrer:in' })}
                  value={
                    <div>
                      {substituteTeacher && substituteTeacher !== originalTeacher ? (
                        <>
                          {originalTeacher && (
                            <div className="strikeThrough">{originalTeacher}</div>
                          )}
                          <div>
                            {t('timetable:period:teacher_substituted_by', {
                              defaultValue: 'Vertreten durch: {{substitution}}',
                              substitution: substituteTeacher,
                            })}
                          </div>
                        </>
                      ) : (
                        originalTeacher
                      )}
                    </div>
                  }
                />
              )}
              {timeTableLesson.room && (
                <TimetablePeriodInfoRow
                  icon={<BuildingRegular />}
                  label={t('timetable:period:room', { defaultValue: 'Kursraum' })}
                  value={
                    <div>
                      {substitution?.substitutionLesson?.room &&
                      substitution?.substitutionLesson?.room !== timeTableLesson.room ? (
                        <>
                          <div className="strikeThrough">{timeTableLesson.room}</div>
                          <div>
                            {t('timetable:period:room_relocated_to', {
                              defaultValue: 'Verlegt nach: {{room}}',
                              room: substitution?.substitutionLesson?.room,
                            })}
                          </div>
                        </>
                      ) : (
                        timeTableLesson.room
                      )}
                    </div>
                  }
                />
              )}
              {timeTableLesson.classes && timeTableLesson.classes.length > 0 && (
                <TimetablePeriodInfoRow
                  icon={<PeopleRegular />}
                  label={t('timetable:period:classes', { defaultValue: 'Klasse(n)' })}
                  value={<div>{classesList}</div>}
                />
              )}
            </div>
          )}
        </div>
      </PopoverSurface>
    </Popover>
  );
};
